import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import NextLink from 'next/link';

import { CharacteristicsPosts, Link, Prose, SocialMedia, Tag } from '@/atoms';
import { Hero } from '@/organisms';

import type { FunctionComponent } from 'react';
import type { TypePostProps } from './types';

/**
 * Post
 */
export const Post: FunctionComponent<TypePostProps> = ({
  averageTimeToRead,
  content,
  hero,
  postDate,
  author,
  socialMedia,
  labels,
}: TypePostProps) => {
  const { t } = useTranslation('common');

  return (
    <div className="bg-surface-01">
      <Hero
        caption={hero.caption}
        title={hero.title}
        textColor="alternative"
        type="essential"
        backgroundImage={hero.bgImage}
        className="min-h-[400px] !justify-center md:mt-16"
        textAling="center"
        hasImage={true}
      />

      <div className="u-wrapper-narrow -translate-y-1/2 px-5 md:px-0">
        <div className="u-aspect-square relative h-16 md:h-20">
          <Image
            src="/assets/img/img/ornamentacion.svg"
            layout="fill"
            objectFit="contain"
            alt="logo Be Casa Essentinal"
          />
        </div>
      </div>

      <div className="u-wrapper-narrow flex flex-col px-5 font-thin md:px-0">
        <CharacteristicsPosts
          author={author}
          averageTimeToRead={averageTimeToRead}
          postDate={postDate instanceof Date ? postDate : new Date(postDate)}
          className="border-b border-primary-200 pb-6 md:pb-10"
        />

        <Prose
          html={content}
          as="article"
          className="mt-6 !text-primary-400 md:mt-10 [&>p>a]:font-bold  [&>p>a]:text-text-primary [&>p>strong]:text-text-primary"
        />

        <div className="mt-10 flex flex-col justify-between gap-4 border-y py-6">
          {socialMedia && (
            <SocialMedia
              variants="primary"
              icons={socialMedia}
              text={t('components.templates.post.Share')}
              iconSize={27}
            />
          )}

          <div className="flex flex-col gap-2 overflow-auto">
            <span>{t('components.templates.post.labels')}</span>

            {labels && labels.length > 0 && (
              <div className="flex flex-row gap-3">
                {labels.map((child, index) => (
                  <NextLink
                    prefetch={false}
                    passHref
                    href={child.href}
                    key={index}
                  >
                    <Tag>{child.text}</Tag>
                  </NextLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <Link
          className="mx-auto my-10"
          onClick={() =>
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
          }
          underline
          variant="primary"
          as="button"
        >
          {t('components.templates.post.scrollTop')}
        </Link>
      </div>
    </div>
  );
};

Post.displayName = 'Post';
